import { useState, useMemo } from 'react';
import { useCustomers } from '../../../hooks/useCustomers';
import type { Customer } from '../../../mocks/crm';
import CustomerList from './components/CustomerList';
import CustomerDetail from './components/CustomerDetail';
import { agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

export default function CustomersPage() {
  const { customers, loading } = useCustomers();
  const [selected, setSelected] = useState<Customer | null>(customers[0] ?? null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return customers;
    const q = search.toLowerCase();
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        c.nationality.toLowerCase().includes(q)
    );
  }, [customers, search]);

  const handleSelect = (c: Customer) => setSelected(c);

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="px-6 py-4 border-b border-gray-100 bg-white flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Customer Profiles</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your travel clients and their history</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-5">
            {[
              { label: 'Total Customers', value: customers.length },
              { label: 'VIP Clients', value: customers.filter((c) => c.tags.includes('VIP')).length },
            ].map((s) => (
              <div key={s.label} className="text-right">
                <p className="text-xl font-bold" style={{ color: brand }}>{s.value}</p>
                <p className="text-xs text-gray-400">{s.label}</p>
              </div>
            ))}
          </div>
          <button
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-user-add-line" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 min-h-0">
        <CustomerList
          customers={filtered}
          selected={selected}
          onSelect={handleSelect}
          search={search}
          onSearch={setSearch}
        />

        {selected ? (
          <CustomerDetail key={selected.id} customer={selected} />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${brand}15` }}
            >
              <i className="ri-contacts-book-2-line text-2xl" style={{ color: brand }} />
            </div>
            <p className="text-base font-semibold text-gray-700 mb-1">Select a customer</p>
            <p className="text-sm text-gray-400">Choose a customer from the list to view their full profile and booking history.</p>
          </div>
        )}
      </div>
    </div>
  );
}
